import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    ImagePlus,
    X,
    ChevronRight,
    Sparkles,
    MapPin,
    Globe,
    Shuffle,
    Lock,
    Wand2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { createService, getServiceById, listServicesByOwner, updateService } from "@/lib/entitiesStore";
import { canCreateMore } from "@/lib/monetizationStore";
import { fileToImageDataUrl } from "@/lib/hooks";
import CategoryPicker from "@/components/CategoryPicker";
import { generateServiceDescription } from "@/lib/aiMock";

const SERVICE_TYPES = [
    { id: "in_person", label: "In-person", icon: MapPin,  caption: "I travel or host at my place" },
    { id: "online",    label: "Online",    icon: Globe,   caption: "Delivered remotely" },
    { id: "hybrid",    label: "Hybrid",    icon: Shuffle, caption: "Both options available" },
];

const PRICING_TYPES = [
    { id: "fixed", label: "Fixed price",   caption: "One flat amount" },
    { id: "from",  label: "Starting from", caption: "Price varies by request" },
    { id: "none",  label: "No price",      caption: "Contact for a quote" },
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const CreateService = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { id: editId } = useParams();
    const isEdit = Boolean(editId);
    const existing = useMemo(() => (isEdit ? getServiceById(editId) : null), [editId, isEdit]);
    const fileRef = useRef(null);
    const isPremium = !!user?.premium;

    const [name, setName] = useState(existing?.name || "");
    const [cat, setCat] = useState({
        category: existing?.categoryGroup || (existing?.customCategory ? "" : existing?.category) || "",
        subcategory: existing?.subcategory || "",
        customLabel: existing?.customCategory || null,
    });
    const [pickerOpen, setPickerOpen] = useState(false);

    const [serviceType, setServiceType] = useState(existing?.serviceType || "in_person");
    const [pricingType, setPricingType] = useState(existing?.pricingType || "");
    const [price, setPrice] = useState(existing?.price ? String(existing.price) : "");

    const [location, setLocation] = useState(existing?.location || "Lagos, Nigeria");
    const [description, setDescription] = useState(existing?.description || "");
    const [aiBusy, setAiBusy] = useState(false);

    const [availabilityOn, setAvailabilityOn] = useState(!!existing?.availability);
    const [availDays, setAvailDays] = useState(existing?.availability?.days || []);
    const [availFrom, setAvailFrom] = useState(existing?.availability?.from || "09:00");
    const [availTo, setAvailTo] = useState(existing?.availability?.to || "18:00");

    const [whatsappEnabled, setWhatsappEnabled] = useState(!!existing?.whatsappEnabled);
    const [phoneEnabled, setPhoneEnabled] = useState(!!existing?.phoneEnabled);

    const [images, setImages] = useState(() => {
        if (!existing) return [];
        if (Array.isArray(existing.images)) return existing.images.slice(0, 5);
        if (existing.image) return [existing.image];
        return [];
    });
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState({});
    const [limitMsg, setLimitMsg] = useState(null);

    useEffect(() => {
        if (isEdit && !existing) navigate("/manage", { replace: true });
    }, [isEdit, existing, navigate]);
    useEffect(() => {
        if (isEdit && existing && user && existing.ownerId && existing.ownerId !== user.id) {
            navigate(`/services/${existing.id}`, { replace: true });
        }
    }, [isEdit, existing, user, navigate]);

    const handleImages = async (files) => {
        if (!files?.length) return;
        const remaining = 5 - images.length;
        const picked = Array.from(files).slice(0, remaining);
        setBusy(true);
        try {
            const urls = [];
            for (const f of picked) {
                if (!f.type.startsWith("image/")) continue;
                urls.push(await fileToImageDataUrl(f, 1200, 0.78));
            }
            setImages((prev) => [...prev, ...urls]);
        } finally {
            setBusy(false);
        }
    };

    const removeImage = (idx) =>
        setImages((prev) => prev.filter((_, i) => i !== idx));

    const categoryLabel = cat.customLabel
        ? cat.customLabel
        : cat.category && cat.subcategory
        ? `${cat.category} · ${cat.subcategory}`
        : cat.category || "";

    const toggleDay = (d) =>
        setAvailDays((prev) =>
            prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
        );

    const handleAIGenerate = async () => {
        setAiBusy(true);
        // Tiny delay to feel like generation
        await new Promise((r) => setTimeout(r, 700));
        const text = generateServiceDescription({
            name,
            category: cat.customLabel || cat.category,
            subcategory: cat.subcategory,
            serviceType,
            pricingType: pricingType || "from",
            price: Number(price) || 0,
            location,
        });
        setDescription(text);
        if (errors.description) setErrors({ ...errors, description: null });
        setAiBusy(false);
    };

    const handleSubmit = () => {
        if (!isEdit) {
            const currentCount = user ? listServicesByOwner(user.id).length : 0;
            if (!canCreateMore(user, "services", currentCount)) {
                setLimitMsg("You've reached your free limit. Upgrade to create unlimited listings.");
                return;
            }
        }

        const e = {};
        if (!name.trim()) e.name = "Service name is required";
        const hasCat = !!(
            cat.customLabel ||
            (cat.category && (cat.subcategory || cat.category === "Other"))
        );
        if (!hasCat) e.category = "Choose a category";
        if (images.length < 1) e.images = "Add at least 1 image";

        if (!pricingType) {
            e.pricingType = "Select a pricing option";
        } else if ((pricingType === "fixed" || pricingType === "from") && !String(price).trim()) {
            e.price = "Enter an amount";
        } else if (pricingType !== "none" && Number(price) < 0) {
            e.price = "Price can't be negative";
        }

        if (serviceType === "online" && pricingType === "none") {
            e.pricingType = "Online services require a price";
        }

        if (!description.trim() || description.trim().length < 10)
            e.description = "At least 10 characters";

        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }

        const finalCategory = cat.customLabel
            ? cat.customLabel
            : cat.subcategory || cat.category;

        const availability =
            availabilityOn && availDays.length
                ? { days: availDays, from: availFrom, to: availTo }
                : null;

        const payload = {
            name,
            category: finalCategory,
            categoryGroup: cat.category || null,
            subcategory: cat.subcategory || null,
            customCategory: cat.customLabel || null,
            serviceType,
            pricingType,
            price: pricingType === "none" ? 0 : Number(price) || 0,
            location,
            description,
            images,
            image: images[0] || null,
            availability,
            whatsappEnabled,
            phoneEnabled,
        };

        if (isEdit) {
            updateService(editId, { ...payload, updatedAt: Date.now() });
            navigate(`/services/${editId}`, { replace: true });
        } else {
            const s = createService(user, payload);
            navigate(`/services/${s.id}`, { replace: true });
        }
    };

    return (
        <div className="screen relative" data-testid="create-service-screen">
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center justify-between">
                <button
                    onClick={() => navigate(-1)}
                    data-testid="create-service-back"
                    className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="font-display text-[16px] font-semibold">{isEdit ? "Edit Service" : "Create Service"}</h1>
                <span className="h-10 w-10" />
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5 space-y-6">
                {/* Photos */}
                <section>
                    <label className="block text-[12px] font-medium text-muted-foreground mb-2 ml-1">
                        Photos ({images.length}/5)
                    </label>
                    <div className="grid grid-cols-3 gap-2" data-testid="create-service-images">
                        {images.map((src, i) => (
                            <div
                                key={i}
                                className="relative aspect-square rounded-xl overflow-hidden border border-border"
                                data-testid={`create-service-image-${i}`}
                            >
                                <img src={src} alt="" className="h-full w-full object-cover" />
                                <button
                                    type="button"
                                    onClick={() => removeImage(i)}
                                    data-testid={`create-service-image-remove-${i}`}
                                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-background/90 border border-border flex items-center justify-center text-destructive"
                                    aria-label="Remove image"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        ))}
                        {images.length < 5 && (
                            <button
                                type="button"
                                data-testid="create-service-image-add"
                                onClick={() => fileRef.current?.click()}
                                disabled={busy}
                                className="aspect-square rounded-xl border-2 border-dashed border-border bg-secondary flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary disabled:opacity-60"
                            >
                                <ImagePlus size={20} />
                                <span className="text-[11px] font-medium">Add</span>
                            </button>
                        )}
                        <input
                            ref={fileRef}
                            data-testid="create-service-image-input"
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => handleImages(e.target.files)}
                        />
                    </div>
                    {errors.images && (
                        <p data-testid="create-service-images-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                            {errors.images}
                        </p>
                    )}
                </section>

                {/* Service name */}
                <Field label="Service name" error={errors.name} testId="create-service-name">
                    <input
                        data-testid="create-service-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Glow Makeup Studio"
                        className="flex-1 bg-transparent outline-none text-[15px]"
                    />
                </Field>

                {/* Category */}
                <div data-testid="create-service-category-field">
                    <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                        Category
                    </span>
                    <button
                        type="button"
                        data-testid="create-service-category"
                        onClick={() => setPickerOpen(true)}
                        className={[
                            "w-full flex items-center gap-2 h-14 px-4 rounded-2xl border bg-secondary text-left transition-colors",
                            errors.category
                                ? "border-destructive bg-destructive/5"
                                : "border-border hover:bg-muted",
                        ].join(" ")}
                    >
                        <span
                            className={[
                                "flex-1 truncate text-[15px]",
                                categoryLabel ? "" : "text-muted-foreground/80",
                            ].join(" ")}
                        >
                            {categoryLabel || "Choose a category"}
                        </span>
                        <ChevronRight size={16} className="text-muted-foreground" />
                    </button>
                    {errors.category && (
                        <p data-testid="create-service-category-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                            {errors.category}
                        </p>
                    )}
                </div>

                {/* Service type */}
                <section data-testid="create-service-type-field">
                    <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                        Service type
                    </span>
                    <div className="grid grid-cols-3 gap-2">
                        {SERVICE_TYPES.map((t) => {
                            const Icon = t.icon;
                            const active = serviceType === t.id;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    data-testid={`create-service-type-${t.id}`}
                                    onClick={() => setServiceType(t.id)}
                                    className={[
                                        "flex flex-col items-center justify-center gap-1 px-2 py-3 rounded-2xl border text-center transition-all",
                                        active
                                            ? "border-primary bg-primary/5 shadow-[0_8px_22px_-12px_hsl(var(--primary)/0.55)]"
                                            : "border-border bg-secondary hover:bg-muted",
                                    ].join(" ")}
                                    aria-pressed={active}
                                >
                                    <Icon size={18} className={active ? "text-primary" : "text-muted-foreground"} />
                                    <span className="text-[12.5px] font-medium leading-tight">{t.label}</span>
                                </button>
                            );
                        })}
                    </div>
                    <p className="mt-1.5 ml-1 text-[11.5px] text-muted-foreground">
                        {SERVICE_TYPES.find((t) => t.id === serviceType)?.caption}
                    </p>
                </section>

                {/* Pricing */}
                <section data-testid="create-service-pricing-field">
                    <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                        Pricing
                    </span>
                    <div className="space-y-2">
                        {PRICING_TYPES.map((p) => {
                            const active = pricingType === p.id;
                            const disabled = serviceType === "online" && p.id === "none";
                            return (
                                <button
                                    key={p.id}
                                    type="button"
                                    disabled={disabled}
                                    data-testid={`create-service-pricing-${p.id}`}
                                    onClick={() => {
                                        setPricingType(p.id);
                                        if (p.id === "none") setPrice("");
                                        if (errors.pricingType || errors.price) {
                                            setErrors({ ...errors, pricingType: null, price: null });
                                        }
                                    }}
                                    className={[
                                        "w-full flex items-center gap-3 p-3.5 rounded-2xl border text-left transition-all",
                                        disabled && "opacity-50 cursor-not-allowed",
                                        active
                                            ? "border-primary bg-primary/5"
                                            : "border-border bg-secondary hover:bg-muted",
                                    ].filter(Boolean).join(" ")}
                                    aria-pressed={active}
                                >
                                    <span
                                        className={[
                                            "h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0",
                                            active ? "border-primary" : "border-muted-foreground/40",
                                        ].join(" ")}
                                    >
                                        {active && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                                    </span>
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-[14px] font-medium leading-tight">{p.label}</span>
                                        <span className="block text-[11.5px] text-muted-foreground mt-0.5">
                                            {disabled ? "Not available for online services" : p.caption}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {errors.pricingType && (
                        <p data-testid="create-service-pricing-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                            {errors.pricingType}
                        </p>
                    )}

                    {(pricingType === "fixed" || pricingType === "from") && (
                        <div className="mt-3" data-testid="create-service-price-wrap">
                            <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                                {pricingType === "fixed" ? "Amount (₦)" : "Starting from (₦)"}
                            </span>
                            <span
                                className={[
                                    "flex items-center gap-2 h-14 px-4 rounded-2xl border transition-colors",
                                    errors.price
                                        ? "border-destructive bg-destructive/5"
                                        : "border-border bg-secondary focus-within:bg-background focus-within:border-primary",
                                ].join(" ")}
                            >
                                <span className="text-muted-foreground text-[15px]">₦</span>
                                <input
                                    data-testid="create-service-price"
                                    type="number"
                                    inputMode="numeric"
                                    value={price}
                                    onChange={(e) => setPrice(e.target.value)}
                                    placeholder={pricingType === "fixed" ? "10,000" : "5,000"}
                                    className="flex-1 bg-transparent outline-none text-[15px]"
                                />
                            </span>
                            {errors.price && (
                                <p data-testid="create-service-price-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                                    {errors.price}
                                </p>
                            )}
                        </div>
                    )}

                    {pricingType === "none" && (
                        <p
                            data-testid="create-service-price-on-request"
                            className="mt-3 px-4 py-3 rounded-2xl bg-secondary/70 border border-dashed border-border text-[12.5px] text-muted-foreground"
                        >
                            Clients will see <span className="font-medium text-foreground">"Price on request"</span>.
                        </p>
                    )}
                </section>

                {/* Location */}
                <Field label="Location" testId="create-service-location">
                    <input
                        data-testid="create-service-location"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className="flex-1 bg-transparent outline-none text-[15px]"
                    />
                </Field>

                {/* Description with AI */}
                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-[12px] font-medium text-muted-foreground ml-1">
                            Description
                        </label>
                        <button
                            type="button"
                            data-testid="create-service-ai-generate"
                            onClick={handleAIGenerate}
                            disabled={aiBusy}
                            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[11.5px] font-medium text-primary border border-primary/30 bg-primary/5 hover:bg-primary/10 disabled:opacity-60 transition-colors"
                        >
                            <Wand2 size={12} />
                            {aiBusy ? "Generating…" : "Generate with AI"}
                        </button>
                    </div>
                    <textarea
                        data-testid="create-service-description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={5}
                        maxLength={600}
                        placeholder="What do you offer?"
                        className={[
                            "w-full p-3.5 rounded-2xl border outline-none text-[14px] resize-none transition-colors",
                            errors.description
                                ? "border-destructive bg-destructive/5"
                                : "border-border bg-secondary focus:bg-background focus:border-primary",
                        ].join(" ")}
                    />
                    {errors.description && (
                        <p data-testid="create-service-description-error" className="text-[12px] text-destructive mt-1.5 ml-1">
                            {errors.description}
                        </p>
                    )}
                </div>

                {/* Availability */}
                <section data-testid="create-service-availability">
                    <ToggleRow
                        testId="create-service-availability-toggle"
                        title="Set availability"
                        caption="Optional — helps clients pick the right time"
                        checked={availabilityOn}
                        onChange={setAvailabilityOn}
                    />
                    {availabilityOn && (
                        <div className="mt-3 space-y-3">
                            <div className="flex flex-wrap gap-1.5" data-testid="create-service-availability-days">
                                {DAYS.map((d) => {
                                    const active = availDays.includes(d);
                                    return (
                                        <button
                                            key={d}
                                            type="button"
                                            data-testid={`create-service-day-${d}`}
                                            onClick={() => toggleDay(d)}
                                            className={[
                                                "px-3 h-9 rounded-full text-[12.5px] font-medium border transition-colors",
                                                active
                                                    ? "bg-primary text-primary-foreground border-primary"
                                                    : "bg-secondary border-border text-foreground hover:bg-muted",
                                            ].join(" ")}
                                            aria-pressed={active}
                                        >
                                            {d}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <TimeField
                                    label="From"
                                    testId="create-service-availability-from"
                                    value={availFrom}
                                    onChange={setAvailFrom}
                                />
                                <TimeField
                                    label="To"
                                    testId="create-service-availability-to"
                                    value={availTo}
                                    onChange={setAvailTo}
                                />
                            </div>
                        </div>
                    )}
                </section>

                {/* Contact settings (premium gated) */}
                <section data-testid="create-service-contact-settings">
                    <span className="flex items-center justify-between mb-2 ml-1">
                        <span className="text-[12px] font-medium text-muted-foreground">
                            Contact settings
                        </span>
                        {!isPremium && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10.5px] font-semibold">
                                <Sparkles size={10} />
                                Premium
                            </span>
                        )}
                    </span>
                    <div className="space-y-2">
                        <ToggleRow
                            testId="create-service-toggle-whatsapp"
                            title="Enable WhatsApp"
                            caption={
                                isPremium
                                    ? "Show a WhatsApp button on your service"
                                    : "Upgrade to Premium to enable"
                            }
                            checked={whatsappEnabled && isPremium}
                            disabled={!isPremium}
                            onChange={setWhatsappEnabled}
                            lockIcon={!isPremium}
                        />
                        <ToggleRow
                            testId="create-service-toggle-phone"
                            title="Show phone number"
                            caption={
                                isPremium
                                    ? "Allow clients to see and call your number"
                                    : "Upgrade to Premium to enable"
                            }
                            checked={phoneEnabled && isPremium}
                            disabled={!isPremium}
                            onChange={setPhoneEnabled}
                            lockIcon={!isPremium}
                        />
                    </div>
                    {!isPremium && (
                        <button
                            type="button"
                            data-testid="create-service-upgrade-link"
                            onClick={() => navigate("/premium")}
                            className="mt-2 ml-1 text-[12px] font-medium text-primary hover:underline"
                        >
                            Upgrade to Premium →
                        </button>
                    )}
                </section>
            </div>

            <div className="px-5 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] border-t border-border/60 bg-background">
                {limitMsg && (
                    <div
                        data-testid="create-service-limit-msg"
                        className="mb-2 px-3 py-2 rounded-xl bg-primary/10 border border-primary/30 text-[12px] text-primary inline-flex items-center justify-between gap-2 w-full"
                    >
                        <span>{limitMsg}</span>
                        <button
                            type="button"
                            data-testid="create-service-upgrade-btn"
                            onClick={() => navigate("/premium")}
                            className="font-semibold underline shrink-0"
                        >
                            Upgrade
                        </button>
                    </div>
                )}
                <button
                    data-testid="create-service-submit"
                    onClick={handleSubmit}
                    disabled={busy}
                    className="w-full h-14 rounded-2xl text-primary-foreground font-medium text-[15px] active:scale-[0.98] transition-transform disabled:opacity-60"
                    style={{
                        background:
                            "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                        boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                    }}
                >
                    {isEdit ? "Save changes" : "Publish service"}
                </button>
            </div>

            <CategoryPicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                value={cat}
                onChange={(v) => {
                    setCat(v);
                    if (errors.category) setErrors({ ...errors, category: null });
                }}
            />
        </div>
    );
};

const Field = ({ label, error, testId, children }) => (
    <label className="block" data-testid={`${testId}-field`}>
        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">{label}</span>
        <span
            className={[
                "flex items-center gap-2 h-14 px-4 rounded-2xl border transition-colors",
                error
                    ? "border-destructive bg-destructive/5"
                    : "border-border bg-secondary focus-within:bg-background focus-within:border-primary",
            ].join(" ")}
        >
            {children}
        </span>
        {error && (
            <span className="block text-[12px] text-destructive mt-1.5 ml-1" data-testid={`${testId}-error`}>
                {error}
            </span>
        )}
    </label>
);

const TimeField = ({ label, testId, value, onChange }) => (
    <label className="block">
        <span className="block text-[11.5px] text-muted-foreground mb-1 ml-1">{label}</span>
        <input
            data-testid={testId}
            type="time"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full h-12 px-4 rounded-2xl border border-border bg-secondary outline-none text-[14px] focus:bg-background focus:border-primary"
        />
    </label>
);

const ToggleRow = ({ testId, title, caption, checked, disabled, onChange, lockIcon }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={[
            "w-full flex items-center gap-3 p-3.5 rounded-2xl border bg-secondary text-left transition-colors",
            disabled ? "opacity-70 cursor-not-allowed" : "hover:bg-muted",
            "border-border",
        ].join(" ")}
    >
        <span className="flex-1 min-w-0">
            <span className="flex items-center gap-1.5">
                <span className="text-[14px] font-medium leading-tight">{title}</span>
                {lockIcon && <Lock size={11} className="text-muted-foreground" />}
            </span>
            {caption && (
                <span className="block text-[11.5px] text-muted-foreground mt-0.5">{caption}</span>
            )}
        </span>
        <span
            className={[
                "relative h-6 w-10 rounded-full transition-colors shrink-0",
                checked ? "bg-primary" : "bg-muted",
            ].join(" ")}
            aria-hidden
        >
            <span
                className={[
                    "absolute top-0.5 h-5 w-5 rounded-full bg-background shadow transition-all",
                    checked ? "left-[18px]" : "left-0.5",
                ].join(" ")}
            />
        </span>
    </button>
);

export default CreateService;
